/**
 * Gestión (artículos, documentos, contactos, asientos) sobre Supabase (ex Firestore).
 * Los ex-writeBatch pasan a awaits secuenciales; las transacciones de tesorería
 * asociadas a documentos van por el RPC atómico de financeService (createTransaction).
 */
import {
  subscribeUser,
  listUserRows,
  listRowsArrayContains,
  getRow,
  insertRow,
  updateRow,
  deleteRow,
  newId,
} from '../lib/db';
import {
  ManagedArticle,
  ManagedDocument,
  JournalEntryLine,
  JournalEntry,
  Transaction,
  AccountingAccount,
  ManagedContact,
  TransactionType,
  TransactionDetail
} from '../types';
import { createTransaction } from './financeService';

/**
 * Removes undefined values from an object to prevent validation issues.
 */
const cleanData = (obj: any): any => {
  if (Array.isArray(obj)) {
    return obj.map(cleanData);
  }
  if (obj !== null && typeof obj === 'object') {
    const newObj: any = {};
    Object.keys(obj).forEach(key => {
      if (obj[key] !== undefined) {
        newObj[key] = cleanData(obj[key]);
      }
    });
    return newObj;
  }
  return obj;
};

export const subscribeToArticles = (userId: string, callback: (articles: ManagedArticle[]) => void) => {
  return subscribeUser<ManagedArticle>('managedArticles', userId, callback, { orderBy: 'name', ascending: true });
};

export const createArticle = async (userId: string, data: Omit<ManagedArticle, 'id' | 'createdAt' | 'userId'>) => {
  return insertRow<ManagedArticle>('managedArticles', cleanData({
    ...data,
    userId,
    createdAt: new Date().toISOString()
  }));
};

export const updateArticle = async (userId: string, id: string, data: Partial<ManagedArticle>) => {
  return updateRow('managedArticles', id, cleanData(data));
};

export const deleteArticle = async (userId: string, id: string) => {
  return deleteRow('managedArticles', id);
};

export const subscribeToDocuments = (userId: string, callback: (docs: ManagedDocument[]) => void) => {
  return subscribeUser<ManagedDocument>('managedDocuments', userId, callback, { orderBy: 'date', ascending: false });
};

export const checkLocatorCodeUnique = async (userId: string, code: string, currentDocId?: string): Promise<boolean> => {
  const rows = await listUserRows<ManagedDocument>('managedDocuments', userId);
  const matches = rows.filter(d => d.locatorCode === code.toUpperCase());
  if (matches.length === 0) return true;
  // If editing, checking uniqueness shouldn't fail against its own current code
  if (currentDocId && matches.length === 1 && matches[0].id === currentDocId) {
    return true;
  }
  return false;
};

export const generateUniqueLocatorCode = async (userId: string): Promise<string> => {
  const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const numbers = '0123456789';

  let attempts = 0;
  while (attempts < 10) {
    let code = '';
    for (let i = 0; i < 2; i++) {
      code += letters.charAt(Math.floor(Math.random() * letters.length));
    }
    for (let i = 0; i < 3; i++) {
      code += numbers.charAt(Math.floor(Math.random() * numbers.length));
    }

    // Convert to uppercase guarantee
    code = code.toUpperCase();

    // Check database to ensure no collision
    if (await checkLocatorCodeUnique(userId, code)) {
      return code;
    }
    attempts++;
  }
  throw new Error("No se pudo generar un código único en la base de datos tras multiples intentos.");
};

export const createManagedDocument = async (
  userId: string,
  data: Omit<ManagedDocument, 'id' | 'createdAt' | 'userId'>,
  newTransactions?: {
    description: string;
    type: TransactionType;
    date: string;
    details: TransactionDetail[];
  }[]
) => {
  if (data.locatorCode) {
    const isUnique = await checkLocatorCodeUnique(userId, data.locatorCode);
    if (!isUnique) throw new Error(`El código de referencia '${data.locatorCode}' ya existe en otro documento.`);
  }

  const docId = newId();

  // Ensure locator code exists
  const finalLocatorCode = data.locatorCode ? data.locatorCode.toUpperCase() : await generateUniqueLocatorCode(userId);

  // nota: sin batch atómico; orden pensado para fallo parcial seguro
  // (1º transacciones de tesorería vía RPC atómico, 2º el documento que las referencia,
  //  3º flags pendingInvoice de transacciones ya existentes)

  // 1. Create new transactions if any (RPC atómico con balances)
  const finalTransactionIds = [...(data.transactionIds || [])];

  if (newTransactions && newTransactions.length > 0) {
    for (const txData of newTransactions) {
      const txId = await createTransaction(userId, { ...txData, pendingInvoice: false });
      if (txId) finalTransactionIds.push(txId);
    }
  }

  const document = cleanData({
    ...data,
    transactionIds: finalTransactionIds,
    appliedDocumentIds: data.applications?.map(a => a.documentId) || [],
    locatorCode: finalLocatorCode,
    id: docId,
    userId,
    createdAt: new Date().toISOString()
  });

  await insertRow('managedDocuments', document);

  // Clear pendingInvoice flag for any linked transaction (ONLY existing ones, new ones are handled above)
  if (data.transactionIds) {
    for (const txId of data.transactionIds) {
      await updateRow('transactions', txId, { pendingInvoice: false });
    }
  }

  // If it's a receipt, update parent document balances
  if (data.applications && data.applications.length > 0) {
    for (const app of data.applications) {
      await recalculateDocumentBalance(userId, app.documentId);
    }
  }

  return document as ManagedDocument;
};

export const updateManagedDocument = async (
  userId: string,
  id: string,
  data: Partial<ManagedDocument>,
  newTransactions?: {
    description: string;
    type: TransactionType;
    date: string;
    details: TransactionDetail[];
  }[]
) => {
  if (data.locatorCode) {
    const isUnique = await checkLocatorCodeUnique(userId, data.locatorCode, id);
    if (!isUnique) throw new Error(`El código de referencia '${data.locatorCode}' ya existe en otro documento.`);
    data.locatorCode = data.locatorCode.toUpperCase();
  }

  // Get old document to find previous applications for recalculation and existing transactions
  const oldData = await getRow<ManagedDocument>('managedDocuments', id);

  const updateData: any = { ...data };
  if (data.applications) {
    updateData.appliedDocumentIds = data.applications.map(a => a.documentId);
  }

  // nota: sin batch atómico; orden pensado para fallo parcial seguro
  // (1º transacciones nuevas vía RPC atómico, 2º el documento con sus ids finales,
  //  3º flags pendingInvoice de transacciones ya existentes)

  // Only handle transactionIds if explicitly provided in data or if new transactions are being added
  if (newTransactions && newTransactions.length > 0) {
    const baseIds = data.transactionIds !== undefined ? data.transactionIds : (oldData?.transactionIds || []);
    const finalIds = [...baseIds];
    for (const txData of newTransactions) {
      const txId = await createTransaction(userId, { ...txData, pendingInvoice: false });
      if (txId) finalIds.push(txId);
    }
    updateData.transactionIds = finalIds;
  } else if (data.transactionIds !== undefined) {
    updateData.transactionIds = data.transactionIds;
  }

  await updateRow('managedDocuments', id, cleanData(updateData));

  // Clear pendingInvoice flag for any existing linked transaction from data.transactionIds
  if (data.transactionIds) {
    for (const txId of data.transactionIds) {
      await updateRow('transactions', txId, { pendingInvoice: false });
    }
  }

  // Recalculate balances for all involved documents
  const affectedDocIds = new Set<string>();
  affectedDocIds.add(id); // Always recalculate the current document as well
  if (oldData?.applications) {
    oldData.applications.forEach(a => affectedDocIds.add(a.documentId));
  }
  if (data.applications) {
    data.applications.forEach(a => affectedDocIds.add(a.documentId));
  }
  if (oldData?.parentDocumentId) affectedDocIds.add(oldData.parentDocumentId);
  if (data.parentDocumentId) affectedDocIds.add(data.parentDocumentId);

  for (const docId of Array.from(affectedDocIds)) {
    if (docId) await recalculateDocumentBalance(userId, docId);
  }
};

export const deleteManagedDocument = async (userId: string, id: string) => {
  const docData = await getRow<ManagedDocument>('managedDocuments', id);

  await deleteRow('managedDocuments', id);

  // Recalculate balances for parents
  const affectedDocIds = new Set<string>();
  if (docData?.applications) {
    docData.applications.forEach(a => affectedDocIds.add(a.documentId));
  }
  if (docData?.parentDocumentId) {
    affectedDocIds.add(docData.parentDocumentId);
  }

  for (const docId of Array.from(affectedDocIds)) {
    if (docId) await recalculateDocumentBalance(userId, docId);
  }
};

// Contacts

export const subscribeToContacts = (userId: string, callback: (contacts: ManagedContact[]) => void) => {
  return subscribeUser<ManagedContact>('managedContacts', userId, callback, { orderBy: 'name', ascending: true });
};

export const createContact = async (userId: string, data: Omit<ManagedContact, 'id' | 'createdAt' | 'userId'>) => {
  return insertRow<ManagedContact>('managedContacts', cleanData({
    ...data,
    userId,
    createdAt: new Date().toISOString()
  }));
};

export const updateContact = async (userId: string, id: string, data: Partial<ManagedContact>) => {
  return updateRow('managedContacts', id, cleanData(data));
};

export const deleteContact = async (userId: string, id: string) => {
  return deleteRow('managedContacts', id);
};

export const settleManagedDocument = async (
  userId: string,
  managedDoc: ManagedDocument,
  accounts: AccountingAccount[],
) => {
  const entryId = newId();
  const lines: JournalEntryLine[] = [];
  // Actualizaciones de saldos de documentos padre, diferidas al final (ex writeBatch)
  const parentUpdates: { id: string; values: Record<string, unknown> }[] = [];

  const descriptions = {
    'PURCHASE_INVOICE': 'Factura Compra',
    'SALES_INVOICE': 'Factura Venta',
    'INTERNAL_EXPENSE': 'Egreso Interno',
    'INTERNAL_INCOME': 'Ingreso Interno',
    'RECEIPT_EXPENSE': 'Recibo Egreso',
    'RECEIPT_INCOME': 'Recibo Ingreso'
  };

  const description = `${descriptions[managedDoc.type] || 'Documento'} - ${managedDoc.contactName} - ${managedDoc.number || 'SN'}${managedDoc.locatorCode ? ` (Ref: ${managedDoc.locatorCode})` : ''}`;

  // Find standard accounts for offset
  const accountsPayable = accounts.find(a => a.code === '2.1.01'); // Cuentas a Pagar Comerciales
  const accountsReceivable = accounts.find(a => a.code === '1.1.03'); // Cuentas a Cobrar

  if (!accountsPayable || !accountsReceivable) {
    throw new Error('No se encontraron las cuentas contables base (2.1.01, 1.1.03). Asegúrate de inicializar el módulo contable.');
  }

  const isExpense = managedDoc.type === 'PURCHASE_INVOICE' || managedDoc.type === 'INTERNAL_EXPENSE' || managedDoc.type === 'RECEIPT_EXPENSE';
  const isReceipt = managedDoc.type === 'RECEIPT_EXPENSE' || managedDoc.type === 'RECEIPT_INCOME';

  if (managedDoc.isHistorical) {
    const retainedEarnings = accounts.find(a => a.code === '3.3');
    if (!retainedEarnings) {
      throw new Error('Cuenta de Resultados Acumulados (3.3) no encontrada.');
    }

    const pendingAmount = managedDoc.historicalPendingAmount || 0;
    if (pendingAmount <= 1) {
      throw new Error('El saldo pendiente histórico debe ser mayor a 1.');
    }

    // Identical logic to ManagementDashboard handleSettleDocument but for service-side atomic execution
    lines.push({
      accountId: isExpense ? retainedEarnings.id : accountsReceivable.id,
      debit: pendingAmount,
      credit: 0,
      description: `Carga Saldo Inicial: ${managedDoc.locatorCode || managedDoc.number}`
    });

    lines.push({
      accountId: isExpense ? accountsPayable.id : retainedEarnings.id,
      debit: 0,
      credit: pendingAmount,
      description: `Carga Saldo Inicial: ${managedDoc.locatorCode || managedDoc.number}`
    });
  } else if (isReceipt) {
    const parentDocs: any[] = [];
    if (managedDoc.applications && managedDoc.applications.length > 0) {
      for (const app of managedDoc.applications) {
        const parent = await getRow<ManagedDocument>('managedDocuments', app.documentId);
        if (parent) {
          parentDocs.push({ ...parent, appliedAmount: app.amount });
        }
      }
    } else if (managedDoc.appliedDocumentIds && managedDoc.appliedDocumentIds.length > 0) {
      // Falta iterar appliedDocumentIds si existe sin amounts en applications
      // (caso fallback: dividimos en partes iguales para evitar error)
      const splitAmount = managedDoc.totalAmount / managedDoc.appliedDocumentIds.length;
      for (const parentId of managedDoc.appliedDocumentIds) {
        const parent = await getRow<ManagedDocument>('managedDocuments', parentId);
        if (parent) {
          parentDocs.push({ ...parent, appliedAmount: splitAmount });
        }
      }
    } else if (managedDoc.parentDocumentId) {
      const parent = await getRow<ManagedDocument>('managedDocuments', managedDoc.parentDocumentId);
      if (parent) {
        parentDocs.push({ ...parent, appliedAmount: managedDoc.totalAmount });
      }
    }

    if (parentDocs.length === 0) {
      throw new Error('Un recibo debe estar vinculado a al menos una factura o documento interno.');
    }

    const isExpenseReceipt = managedDoc.type === 'RECEIPT_EXPENSE';
    const discountAccountId = isExpenseReceipt ? accountsPayable.id : accountsReceivable.id;

    for (const p of parentDocs) {
      lines.push({
        accountId: discountAccountId,
        debit: isExpenseReceipt ? p.appliedAmount : 0,
        credit: isExpenseReceipt ? 0 : p.appliedAmount,
        description: `Cancelación: ${p.type.replace('_', ' ')} ${p.number || p.locatorCode}`
      });

      // Update parent document balance (diferido, ex writeBatch)
      const newRemaining = Math.max(0, p.remainingAmount - p.appliedAmount);
      parentUpdates.push({
        id: p.id,
        values: {
          remainingAmount: newRemaining,
          isCancelled: newRemaining <= 0
        }
      });
    }

    // La contrapartida es la cuenta de origen del pago (Caja/Banco)
    let paymentAccountId = '';
    if (managedDoc.transactionIds && managedDoc.transactionIds.length > 0) {
      for (const txId of managedDoc.transactionIds) {
        if (paymentAccountId) break;
        const tx = await getRow<Transaction>('transactions', txId);
        if (tx) {
          for (const detail of tx.details) {
            const linkedAcc = accounts.find(a => a.linkedFinanceAccountId === detail.accountId);
            if (linkedAcc) {
              paymentAccountId = linkedAcc.id;
              break;
            }
          }
        }
      }
    }

    if (!paymentAccountId) {
      const cashAcc = accounts.find(a => a.code.startsWith('1.1.01') && a.isSelectable);
      if (!cashAcc) throw new Error('No se encontró una cuenta de Caja para el recibo.');
      paymentAccountId = cashAcc.id;
    }

    lines.push({
      accountId: paymentAccountId,
      debit: isExpenseReceipt ? 0 : managedDoc.totalAmount,
      credit: isExpenseReceipt ? managedDoc.totalAmount : 0,
      description: `Pago Recibo ${managedDoc.number || managedDoc.locatorCode}`
    });

  } else {
    // 1. Lines for Items (Articles)
    for (const item of managedDoc.items) {
      if (!item.accountingAccountId) {
         throw new Error(`El artículo seleccionado no tiene una cuenta contable vinculada.`);
      }

      lines.push({
        accountId: item.accountingAccountId,
        debit: isExpense ? item.total : 0,
        credit: isExpense ? 0 : item.total,
        description: managedDoc.items.length > 1 ? `Item: ${item.total}` : ''
      });
    }

    // 2. Offset Lines (Cash or Accounts Payable/Receivable)
    if (managedDoc.condition === 'CONTADO' && managedDoc.transactionIds && managedDoc.transactionIds.length > 0) {
      // If Contado, we look at the financial transactions to see which account was used
      // Loop through all transactions to find a linked accounting account
      let offsetAccountId = '';

      for (const txId of managedDoc.transactionIds) {
        if (offsetAccountId) break;
        const tx = await getRow<Transaction>('transactions', txId);
        if (tx) {
          for (const detail of tx.details) {
            const linkedAcc = accounts.find(a => a.linkedFinanceAccountId === detail.accountId);
            if (linkedAcc) {
              offsetAccountId = linkedAcc.id;
              break;
            }
          }
        }
      }

      if (!offsetAccountId) {
        // Fallback: Default Cash (1.1.01)
        const cashAcc = accounts.find(a => a.code.startsWith('1.1.01') && a.isSelectable);
        if (!cashAcc) throw new Error('No se encontró una cuenta de Caja/Efectivo para el asiento al contado.');
        offsetAccountId = cashAcc.id;
      }

      lines.push({
        accountId: offsetAccountId,
        debit: isExpense ? 0 : managedDoc.totalAmount,
        credit: isExpense ? managedDoc.totalAmount : 0
      });
    } else {
      // If Credit or no transactions, use Accounts Payable/Receivable
      const offsetAccountId = isExpense ? accountsPayable.id : accountsReceivable.id;
      lines.push({
        accountId: offsetAccountId,
        debit: isExpense ? 0 : managedDoc.totalAmount,
        credit: isExpense ? managedDoc.totalAmount : 0
      });
    }
  }

  const journalEntry: Omit<JournalEntry, 'id'> = {
    date: managedDoc.date,
    description,
    type: (managedDoc.isHistorical ? 'APERTURA' : 'OPERATIVO') as any,
    userId,
    documentLocatorCode: managedDoc.locatorCode || null,
    lines,
    createdAt: new Date().toISOString()
  };

  // nota: sin batch atómico; orden pensado para fallo parcial seguro
  // (1º el asiento nuevo, 2º saldos de documentos padre, 3º el vínculo documento→asiento)
  await insertRow('accountingEntries', cleanData({ ...journalEntry, id: entryId }));

  for (const upd of parentUpdates) {
    await updateRow('managedDocuments', upd.id, upd.values);
  }

  // Link document
  await updateRow('managedDocuments', managedDoc.id, {
    journalEntryId: entryId
  });

  return entryId;
};

export const recalculateDocumentBalance = async (userId: string, documentId: string) => {
  const docData = await getRow<ManagedDocument>('managedDocuments', documentId);
  if (!docData) throw new Error("Documento no encontrado.");

  let hasChanges = false;
  const updates: Partial<ManagedDocument> = {};

  // 0. If it's a receipt, ensure its appliedDocumentIds index is correct and propagate recalc
  if (docData.type === 'RECEIPT_EXPENSE' || docData.type === 'RECEIPT_INCOME') {

    // 0a. Check if totalAmount is 0 but it has linked transactions.
    if (!docData.totalAmount && docData.transactionIds && docData.transactionIds.length > 0) {
      let txnsAmount = 0;
      for (const txId of docData.transactionIds) {
        const tx = await getRow<Transaction>('transactions', txId);
        if (tx) {
          const credits = tx.details.filter(d => d.type === 'CREDIT').reduce((s, d) => s + d.amount, 0);
          const debits = tx.details.filter(d => d.type === 'DEBIT').reduce((s, d) => s + d.amount, 0);
          txnsAmount += Math.max(credits, debits);
        }
      }
      if (txnsAmount > 0) {
        updates.totalAmount = txnsAmount;
        docData.totalAmount = txnsAmount;
        hasChanges = true;
      }
    }

    // 0b. Auto-link floating receipts functionality
    if ((!docData.applications || docData.applications.length === 0) && docData.contactId && docData.totalAmount > 0) {
      const allDocs = await listUserRows<ManagedDocument>('managedDocuments', userId);
      let autoApplications: { documentId: string; amount: number }[] = [];
      let remainingToApply = docData.totalAmount;

      const relevantList = allDocs
        .filter(d => d.contactId === docData.contactId)
        .filter(d => {
          if (d.id === documentId) return false;
          const dirMatch = docData.type === 'RECEIPT_EXPENSE'
            ? (d.type === 'PURCHASE_INVOICE' || d.type === 'INTERNAL_EXPENSE')
            : (d.type === 'SALES_INVOICE' || d.type === 'INTERNAL_INCOME');
          return dirMatch && (d.condition === 'CREDITO' || d.isHistorical) && d.remainingAmount > 0;
        })
        .sort((a, b) => a.date.localeCompare(b.date)); // Oldest first

      for (const d of relevantList) {
        if (remainingToApply <= 0) break;
        const amountToApply = Math.min(d.remainingAmount, remainingToApply);
        if (amountToApply > 0) {
          autoApplications.push({ documentId: d.id, amount: amountToApply });
          remainingToApply -= amountToApply;
        }
      }

      if (autoApplications.length > 0) {
        updates.applications = autoApplications;
        updates.appliedDocumentIds = autoApplications.map(a => a.documentId);
        updates.parentDocumentId = autoApplications[0].documentId; // Legacy support
        docData.applications = autoApplications;
        docData.appliedDocumentIds = updates.appliedDocumentIds;
        hasChanges = true;
      }
    }

    const actualApplied = docData.applications?.map(a => a.documentId) || [];
    const currentApplied = docData.appliedDocumentIds || [];

    const needsFix = JSON.stringify([...currentApplied].sort()) !== JSON.stringify([...actualApplied].sort());

    if (needsFix) {
      updates.appliedDocumentIds = actualApplied;
      hasChanges = true;
    }

    if (hasChanges) {
      await updateRow('managedDocuments', documentId, updates as Record<string, unknown>);
    }

    // Propagate to target invoices to ensure their balance reflects this receipt
    if (actualApplied.length > 0) {
      for (const targetId of actualApplied) {
        // Recursive call to calculate the invoice balance
        await recalculateInvoiceBalance(userId, targetId);
      }
    }
    return 0;
  }

  return await recalculateInvoiceBalance(userId, documentId, docData);
};

// Internal helper to avoid direct infinite recursion and clarify purpose
const recalculateInvoiceBalance = async (userId: string, documentId: string, preloadedData?: ManagedDocument) => {
  let docData = preloadedData;

  if (!docData) {
    const row = await getRow<ManagedDocument>('managedDocuments', documentId);
    if (!row) return 0;
    docData = row;
  }

  // 1. Calculate direct payments from linked transactions
  let totalPayments = 0;
  if (docData.transactionIds && docData.transactionIds.length > 0) {
    for (const txId of docData.transactionIds) {
      const tx = await getRow<Transaction>('transactions', txId);
      if (tx) {
        const credits = tx.details.filter(d => d.type === 'CREDIT').reduce((s, d) => s + d.amount, 0);
        const debits = tx.details.filter(d => d.type === 'DEBIT').reduce((s, d) => s + d.amount, 0);
        totalPayments += Math.max(credits, debits);
      }
    }
  }

  // 2. Calculate payments from Money Receipts linked as children
  // 2a. Legacy (parentDocumentId) - ONLY if it doesn't have the new 'applications' array to avoid double counting
  // 2b. Multi-application (appliedDocumentIds containing documentId)
  const [allDocs, multiRows] = await Promise.all([
    listUserRows<ManagedDocument>('managedDocuments', userId),
    listRowsArrayContains<ManagedDocument>('managedDocuments', userId, 'appliedDocumentIds', documentId)
  ]);

  const legacyRows = allDocs.filter(d =>
    (d.type === 'RECEIPT_EXPENSE' || d.type === 'RECEIPT_INCOME') && d.parentDocumentId === documentId
  );
  const multiReceipts = multiRows.filter(d => d.type === 'RECEIPT_EXPENSE' || d.type === 'RECEIPT_INCOME');

  legacyRows.forEach(rData => {
    if (!rData.applications || rData.applications.length === 0) {
      totalPayments += rData.totalAmount;
    }
  });

  multiReceipts.forEach(rData => {
    if (rData.applications) {
      const app = rData.applications.find(a => a.documentId === documentId);
      if (app) totalPayments += app.amount;
    }
  });

  const baseAmount = docData.isHistorical ? (docData.historicalPendingAmount || 0) : docData.totalAmount;
  const newRemaining = Math.max(0, baseAmount - totalPayments);

  await updateRow('managedDocuments', documentId, {
    remainingAmount: newRemaining,
    isCancelled: newRemaining <= 0
  });

  return newRemaining;
};

export const bulkRecalculateBalances = async (userId: string) => {
  const rows = await listUserRows<ManagedDocument>('managedDocuments', userId);
  const documentIds = rows.map(d => d.id);

  // We'll do this sequentially or in small chunks to avoid overwhelming the client
  // But for a reasonable number of documents, sequential await is safest
  for (const docId of documentIds) {
    try {
      await recalculateDocumentBalance(userId, docId);
    } catch (err) {
      console.error(`Error recalculating balance for ${docId}:`, err);
    }
  }
};

export const removeTransactionLink = async (userId: string, documentId: string, transactionId: string) => {
  const docData = await getRow<ManagedDocument>('managedDocuments', documentId);
  if (!docData) throw new Error("Documento no encontrado.");

  const newTransactionIds = (docData.transactionIds || []).filter(id => id !== transactionId);

  await updateRow('managedDocuments', documentId, {
    transactionIds: newTransactionIds
  });

  // Automatically recalculate balance after unlinking
  await recalculateDocumentBalance(userId, documentId);
};
